import { isArray, isBoolean, isNumber, isObject, isString } from 'lodash';

import { prettifyKey } from '~/Utils';

// An index tells the reader nothing about which item failed, so name it from the matching entry in
// `values` when that entry carries a human label. Falls back to the index when it does not.
const itemLabel = (valueItem: unknown, index: number): string => {
  if (isObject(valueItem)) {
    // `desc`/`display_name` only, deliberately not `id`: this hook also renders on the claimant-facing
    // FNOL form, where ids are UUIDs - a UUID in the message is worse than the index it replaced.
    const candidate = [
      (valueItem as Record<string, unknown>).desc,
      (valueItem as Record<string, unknown>).display_name,
    ].find((value) => isString(value) && value.trim());
    if (isString(candidate)) return candidate;
  }
  return `[${index}]`;
};

/** Lets a caller replace the label for an array item, e.g. to name the section an admin actually edited
 *  rather than the object path the value happens to be stored under. Return undefined to keep the default. */
export type ResolveItemPath = (item: unknown, index: number, path: string[]) => string[] | undefined;

export const traverseErrors = (
  obj: object | string | number | boolean | null,
  path: string[] = [],
  values?: unknown,
  resolveItemPath?: ResolveItemPath
): string[] => {
  const lines: string[] = [];

  if (!obj) return lines;

  if (isString(obj) || isNumber(obj) || isBoolean(obj)) {
    return path.length ? [`${path.join(' → ')} → ${obj}`] : [`${obj}`];
  }

  if (isObject(obj)) {
    for (const [key, value] of Object.entries(obj)) {
      const newPath = [...path, prettifyKey(key)];
      const valueNode = isObject(values) ? (values as Record<string, unknown>)[key] : undefined;

      if (isArray(value)) {
        // Formik fills an errors array sparsely - only the indices that failed. The holes are `undefined`,
        // and printing them produced a wall of "-> undefined" lines above the errors that mattered.
        const valueArray = isArray(valueNode) ? valueNode : [];
        value.forEach((item, index) => {
          if (!item) return;
          const arrayPath = resolveItemPath?.(valueArray[index], index, newPath) ?? [
            ...newPath,
            itemLabel(valueArray[index], index),
          ];
          if (isObject(item)) {
            lines.push(...traverseErrors(item, arrayPath, valueArray[index], resolveItemPath));
          } else {
            lines.push(`${arrayPath.join(' → ')} → ${item}`);
          }
        });
      } else if (isObject(value)) {
        lines.push(...traverseErrors(value, newPath, valueNode, resolveItemPath));
      } else {
        lines.push(`${newPath.join(' → ')} → ${value}`);
      }
    }
  }

  return lines;
};
